import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Icon, Avatar } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { get, status } from "../../api/Support"

/* Widgets */
import { toast } from "../common"
import { Stars } from "../review"

/* Shared */
import { STATUSES, ROLES, typeOf, statusOf } from "./shared"
import { RATE_WORDS, rateAccentOf, ratingOf } from "../review/shared"
import { ago, initialsOf, avatarColorOf, digitsOf } from "../user/shared"
import { organizationIcon } from "../organization/shared"
import { plural } from "../category/shared"

/* Constants */
import { env } from "../../constants"

/* Moment */
import moment from "moment"

Modal.setAppElement("#root")


/* Widget Support Modal — one message of a client or a master: who wrote it, what it is about
   (a point, a review), the way of the case from new to resolved and the note of the team.
   It is opened by the reference: this._support.current.open(item) */
class SupportModal extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            status: "loading",
            data: null,

            /* The line of the list — the top of the card is drawn while the rest loads */
            item: null,

            note: "",
            waiting: false
        }
    }

    open = item => {
        this.setState({ visible: true, item, status: "loading", data: null, note: "", waiting: false }, () => this.load())
    }

    close = () => {

        if (this.state.waiting) {
            return
        }

        this.setState({ visible: false })
    }

    load = () => {

        const { item } = this.state

        get({ kind: item.kind, id: item.id }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data, note: response.data.message.note ? response.data.message.note : "" })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => this.setState({ status: "error" }))
    }

    /* The review behind the complaint was changed or removed in its own card */
    reload = () => {
        if (this.state.visible) {
            this.load()
        }
    }

    copy = text => {

        if (!navigator.clipboard) {
            return
        }

        navigator.clipboard.writeText(text).then(() => toast.success("Скопировано")).catch(() => { })
    }

    /* Move the case — the note written so far goes with it */
    move = (next, text = null) => {

        /* Fields */
        const { data, note, waiting } = this.state
        const { onChanged } = this.props

        if (waiting) {
            return
        }

        const message = data.message

        this.setState({ waiting: text !== null ? "note" : next })

        status({ kind: message.kind, id: message.id, status: next, note: text !== null ? text : note }).then(response => {

            this.setState({ waiting: false })

            if (response && response.status === 200) {

                if (text !== null) {
                    toast.success("Заметка сохранена")
                }
                else {
                    toast.success(next === "done" ? "Отмечено решённым" : `Статус: ${statusOf(next).text.toLowerCase()}`)
                }

                this.load()
                onChanged()
            }
            else {
                toast.error("Что-то пошло не так! Попробуйте позднее")
            }
        }).catch(() => {
            this.setState({ waiting: false })
            toast.error("Что-то пошло не так! Попробуйте позднее")
        })
    }

    /* The top of the card — what it is, who wrote it and when */
    _hero = () => {

        /* Fields */
        const { item, data } = this.state

        const message = data ? data.message : item
        const type = typeOf(message.type)
        const name = message.name && `${message.name}`.trim().length > 0 ? `${message.name}`.trim() : null
        const image = message.image ? `${env.mediapoint}${message.kind === "member" ? "members" : "users"}/${message.image}` : null

        return (
            <div className={`support-hero ${type.accent}`}>

                <div className="support-hero-icon">
                    <Icon name={type.icon} size={26} width={1.8} />
                </div>

                <div className="support-hero-text">
                    <span>{type.text}{message.source ? ` · ${message.source}` : ""}</span>
                    <div className="support-hero-author" style={{ "--avatar": avatarColorOf(message.authorID ? message.authorID : message.id) }}>
                        <Avatar className="support-avatar" src={image} alt={name}>
                            {name ? <span>{initialsOf(name)}</span> : <Icon name="user" size={16} width={2} />}
                        </Avatar>
                        <b>{name ? name : "Без имени"}</b>
                    </div>
                    <em>{moment(message.createdAt).format("D MMMM YYYY, HH:mm")} · {ago(message.createdAt)}</em>
                </div>

            </div>
        )
    }

    /* The way of the case — three steps, any of them is pressed */
    _steps = () => {

        /* Fields */
        const { data, waiting } = this.state

        const message = data.message
        const keys = Object.keys(STATUSES)
        const current = keys.indexOf(message.status)

        return (
            <div className="support-steps">
                {keys.map((key, index) => {

                    const step = STATUSES[key]

                    return (
                        <div
                            key={key}
                            onClick={() => key !== message.status && this.move(key)}
                            className={`support-step ${step.accent} ${index <= current ? "passed" : ""} ${key === message.status ? "current" : ""}`}
                        >
                            <div className="support-step-dot">
                                {waiting === key ? <LoadingOutlined /> : <Icon name={step.icon} size={13} width={2.4} />}
                            </div>
                            <b>{step.text}</b>
                        </div>
                    )
                })}
                {message.status === "done" && message.resolvedAt &&
                    <div className="support-steps-note">
                        {message.resolver ? `${message.resolver}, ` : ""}{moment(message.resolvedAt).format("D MMM YYYY, HH:mm")}
                    </div>
                }
            </div>
        )
    }

    _message = () => {

        /* Fields */
        const { data } = this.state

        const message = data.message

        return (
            <div className="user-section">
                <div className="user-section-title">Сообщение</div>
                {message.message && `${message.message}`.trim().length > 0
                    ? <blockquote className="support-quote">{`${message.message}`.trim()}</blockquote>
                    : <div className="user-empty">Без текста</div>
                }
            </div>
        )
    }

    /* What the complaint is about */
    _target = () => {

        /* Fields */
        const { data } = this.state
        const { onReview } = this.props

        const message = data.message
        const target = data.target

        if (message.type === "organization") {

            if (!target) {
                return (
                    <div className="user-section">
                        <div className="user-section-title">Точка</div>
                        <div className="user-empty">Точка удалена</div>
                    </div>
                )
            }

            const phone = digitsOf(target.phone)

            return (
                <div className="user-section">

                    <div className="user-section-title">
                        Точка
                        {target.complaints > 1 && <span className="support-warning">{target.complaints} {plural(target.complaints, "жалоба", "жалобы", "жалоб")} на неё всего</span>}
                    </div>

                    <div className="support-target">

                        <div className="support-target-head">
                            <Avatar className="support-target-avatar" src={target.image ? `${env.mediapoint}organizations/${target.image}` : null} alt={target.name}>
                                {organizationIcon()}
                            </Avatar>
                            <div>
                                <b>{target.name}</b>
                                <span>{target.address ? target.address : "Адрес не указан"}</span>
                            </div>
                        </div>

                        <div className="support-target-stats">
                            <div><b>★ {ratingOf(target.rating)}</b><span>{target.reviews} {plural(target.reviews, "отзыв", "отзыва", "отзывов")}</span></div>
                            <div><b>{target.bookings}</b><span>{plural(target.bookings, "запись", "записи", "записей")}</span></div>
                            <div className={target.complaints > 1 ? "warning" : ""}><b>{target.complaints}</b><span>{plural(target.complaints, "жалоба", "жалобы", "жалоб")}</span></div>
                        </div>

                        {target.phone &&
                            <div className="user-hero-contacts">
                                <div onClick={() => this.copy(target.phone)} className="user-contact" title="Скопировать"><Icon name="otp" size={14} width={2} />{target.phone}</div>
                                {phone.length >= 10 && <a href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer" className="user-contact whatsapp"><Icon name="support" size={14} width={2} />WhatsApp точки</a>}
                            </div>
                        }

                    </div>

                </div>
            )
        }

        if (message.type === "review") {

            if (!target) {
                return (
                    <div className="user-section">
                        <div className="user-section-title">Отзыв</div>
                        <div className="support-gone">
                            <Icon name="check" size={16} width={2.2} />
                            Отзыва уже нет на платформе — его удалили
                        </div>
                    </div>
                )
            }

            const author = target.userName && `${target.userName}`.trim().length > 0 ? target.userName : target.user

            return (
                <div className="user-section">

                    <div className="user-section-title">Отзыв, на который жалуются</div>

                    <div onClick={() => onReview(target)} className={`support-review ${rateAccentOf(target.rate)}`}>

                        <div className="support-review-head">
                            <Stars value={target.rate} size={14} />
                            <b>{RATE_WORDS[target.rate]}</b>
                            <span>{ago(target.createdAt)}</span>
                        </div>

                        {target.comment
                            ? <p>{target.comment}</p>
                            : <p className="empty">Только оценка, без текста</p>
                        }

                        <div className="support-review-meta">
                            <span><Icon name="user" size={12} width={2.2} />{author ? `${author}`.trim() : "Без имени"}</span>
                            {target.member && <span>Мастер: {target.member}</span>}
                            <span><Icon name="organization" size={12} width={2.2} />{target.organization}</span>
                            {target.photos > 0 && <span><Icon name="image" size={12} width={2.2} />{target.photos}</span>}
                        </div>

                        <div className="support-review-open">Открыть отзыв — удалить или ответить <Icon name="right" size={13} width={2.4} /></div>

                    </div>

                </div>
            )
        }

        return null
    }

    /* Who wrote — a client with an account, a guest of the site or a master */
    _author = () => {

        /* Fields */
        const { data } = this.state
        const { onClient } = this.props

        const message = data.message
        const author = data.author
        const phone = digitsOf(message.phone)

        const contacts = (
            <div className="user-hero-contacts">
                {message.phone && <div onClick={() => this.copy(message.phone)} className="user-contact" title="Скопировать"><Icon name="otp" size={14} width={2} />{message.phone}</div>}
                {phone.length >= 10 && <a href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer" className="user-contact whatsapp"><Icon name="support" size={14} width={2} />WhatsApp</a>}
                {phone.length >= 10 && <a href={`tel:+${phone}`} className="user-contact"><Icon name="power" size={14} width={2} />Позвонить</a>}
                {message.email && <a href={`mailto:${message.email}`} className="user-contact"><Icon name="notification" size={14} width={2} />{message.email}</a>}
            </div>
        )

        if (message.kind === "member") {
            return (
                <div className="user-section">
                    <div className="user-section-title">Мастер</div>
                    {contacts}
                    {author && author.organizations.length > 0 &&
                        <div className="support-teams">
                            {author.organizations.map(organization =>
                                <div key={`${organization.id}`} className={`support-team ${organization.active ? "" : "left"}`}>
                                    <Avatar className="support-team-avatar" src={organization.image ? `${env.mediapoint}organizations/${organization.image}` : null} alt={organization.name}>
                                        {organizationIcon()}
                                    </Avatar>
                                    <b>{organization.name}</b>
                                    <span>{organization.active ? (ROLES[organization.role] ? ROLES[organization.role] : organization.role) : "ушёл из команды"}</span>
                                </div>
                            )}
                        </div>
                    }
                    {author && author.organizations.length === 0 && <div className="user-empty">Мастер не состоит ни в одной точке</div>}
                </div>
            )
        }

        return (
            <div className="user-section">

                <div className="user-section-title">
                    {message.type === "partner" ? "Кто оставил заявку" : "Клиент"}
                    {!message.authorID && <span>{message.type === "partner" ? "с лендинга bookify.kz/partnership" : "без входа в аккаунт"}</span>}
                </div>

                {contacts}

                {author &&
                    <div className="support-client">
                        <div><b>{author.bookings}</b><span>{plural(author.bookings, "запись", "записи", "записей")}</span></div>
                        <div><b>{author.completed}</b><span>состоялись</span></div>
                        <div><b>{author.reviews}</b><span>{plural(author.reviews, "отзыв", "отзыва", "отзывов")}</span></div>
                        <div><b>{moment(author.createdAt).format("MMM YYYY")}</b><span>в Bookify с</span></div>
                        <div onClick={() => onClient(author)} className="support-client-link">Карточка клиента <Icon name="right" size={13} width={2.4} /></div>
                    </div>
                }

            </div>
        )
    }

    /* The note of the team — the author never sees it */
    _note = () => {

        /* Fields */
        const { data, note, waiting } = this.state

        const saved = data.message.note ? data.message.note : ""
        const dirty = note.trim() !== saved.trim()

        return (
            <div className="user-section">
                <div className="user-section-title">Заметка<span>видят только администраторы</span></div>
                <div className="support-note">
                    <textarea maxLength={2000} placeholder="Что сделали, кому позвонили, что ответили" value={note} onChange={event => this.setState({ note: event.target.value })} />
                    {dirty &&
                        <div className="user-actions-buttons">
                            <div onClick={() => this.setState({ note: saved })} className="groups-modal-remove-button light">Отмена</div>
                            <div onClick={() => this.move(data.message.status, note)} className={`groups-modal-remove-button primary ${waiting ? "disabled" : ""}`}>
                                {waiting === "note" ? <LoadingOutlined /> : "Сохранить заметку"}
                            </div>
                        </div>
                    }
                </div>
            </div>
        )
    }

    /* The other messages of the same author */
    _history = () => {

        /* Fields */
        const { data } = this.state

        if (data.history.length === 0) {
            return null
        }

        return (
            <div className="user-section">
                <div className="user-section-title">Ещё от этого автора<span>{data.history.length}</span></div>
                <div className="support-history">
                    {data.history.map(item => {

                        const type = typeOf(item.type)
                        const state = statusOf(item.status)

                        return (
                            <div key={item.key} onClick={() => this.open(item)} className="support-history-item">
                                <div className={`support-history-icon ${type.accent}`}><Icon name={type.icon} size={14} width={2} /></div>
                                <div className="support-history-text">
                                    <b>{item.message ? item.message : type.text}</b>
                                    <span>{type.text} · {ago(item.createdAt)}</span>
                                </div>
                                <div className={`user-badge ${state.accent}`}>{state.text}</div>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    /* The fast way out — resolve and close */
    _actions = () => {

        /* Fields */
        const { data, waiting } = this.state

        const message = data.message

        if (message.status === "done") {
            return (
                <div className="review-actions">
                    <div className="support-done"><Icon name="check" size={16} width={2.4} />Обращение решено</div>
                    <div onClick={() => this.move("progress")} className="review-actions-button">Вернуть в работу</div>
                </div>
            )
        }

        return (
            <div className="review-actions">
                {message.status === "new"
                    ? <div onClick={() => this.move("progress")} className="review-actions-button"><Icon name="clock" size={15} width={2} />Взять в работу</div>
                    : <div />
                }
                <div onClick={() => this.move("done")} className={`support-resolve ${waiting ? "disabled" : ""}`}>
                    {waiting === "done" ? <LoadingOutlined /> : <><Icon name="check" size={16} width={2.4} />Отметить решённым</>}
                </div>
            </div>
        )
    }

    _body = () => {

        /* Fields */
        const { status } = this.state

        if (status === "loading") {
            return <div className="wide-modal-loading"><LoadingOutlined /></div>
        }

        if (status !== "ok") {
            return (
                <div className="wide-modal-loading">
                    <Icon name="alert" size={38} width={1.5} />
                    <p>Не удалось загрузить обращение</p>
                </div>
            )
        }

        return (
            <>
                {this._steps()}
                {this._message()}
                {this._target()}
                {this._author()}
                {this._note()}
                {this._history()}
                {this._actions()}
            </>
        )
    }

    render = () => {

        /* Fields */
        const { visible, item } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="wide-modal large support-modal"
                overlayClassName="wide-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="wide-modal-header">
                    <h2>Обращение</h2>
                    <div onClick={this.close} className="wide-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                {item &&
                    <div className="wide-modal-body">
                        {this._hero()}
                        {this._body()}
                    </div>
                }

            </Modal>
        )
    }
}

export default SupportModal
